import {
  deleteMassEquipments,
  getEquipments,
} from '@services/queries/equipments';
import { useMachine } from '@xstate/react';
import { useCallback, useContext } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useAppDispatch, useAppSelector } from '@store/redux/store';

import { DeleteDialog } from '@components';
import { useHookstate } from '@hookstate/core';
import { NavigationContext } from '@layout/navigation/navigation';
import { useSetState, useToggle } from '@mantine/hooks';
import { IEquipmentStatus } from '@models/EquipmentStatus.model';
import { makeParamsObject } from '@services/queries/equipments/getEquipments/makeParamsObject';
import { IUserMember } from '@services/queries/studios/getUserMembers/getUserMembers';
import { equipmentsPicker } from '@store/machines/equipmentsPicker';
import { EquipmentsMap } from '@store/machines/equipmentsPicker.schema';
import {
  MassEditContext,
  MassEditEvents,
} from '@store/machines/massEdit.schema';
import { updateFilterLastSearch } from '@store/redux/features/filter';
import { resetKnownData } from '@store/redux/features/inventory/knownData';

import { getFilterRequest } from '@utils';
import { ExtractEvent } from 'xstate';
import { updateFilterUpdatedAt } from '@store/redux/features/filter/filterSlice';
import { toggleOff } from '@store/redux/features/mixpanel';

import {
  AddEquipmentModal,
  ImportInventory,
  ListProducts,
  ManualAdd,
  OnBoardingModal,
} from './components';
import { Container } from './Products.styles';
import { trackInventorySearch } from '../../../services/mixpanel/trackers/InventorySearchMetrics';
import { onDeleteEquipments } from './logic/onDeleteEquipments';
import { getEditMode } from './logic/getEditMode';
import Heading from './Heading';
import { isMassEditing } from './logic/isMassEditing';
import { IManualAddState } from './Heading.types';
import { NeededFromEq } from './components/OnBoardingModal/OnBoardingModal.utils';

export const GROUP_EDIT_LIMIT = 60;

export const SEARCH_INVENTORY_KEY = 'inventory';
const SELECT_ALL_KEY = ['inventory', 'equipments', 'all'] as const;

export const INVENTORY_KEY_PREFIX = [
  'inventory',
  'equipments',
  'pages',
] as const;
export interface IFilterData {
  name: string;
  active: boolean;
  id?: string;
}

export interface IFilterTagData {
  id: string;
  name: string;
  tagCategoryId: string;
}
export interface IFilterStatusData {
  status: IEquipmentStatus;
  active: boolean;
}
export interface IFilterUserData {
  user: IUserMember;
  active: boolean;
}
export interface IFilter {
  modelsName: IFilterData[];
  manufactorNames: IFilterData[];
  status: IFilterStatusData[];
  users: IFilterUserData[];
  tags: IFilterTagData[];
  startDate?: Date | null;
  endDate?: Date | null;
  sort?: string;
  direction?: 'ASC' | 'DESC' | undefined;
}

const Products = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [massEditModal, toggleMassEditModal] = useToggle();
  const openDeleteDialog = useHookstate(false);

  const [state, send] = useMachine(equipmentsPicker, {
    actions: {
      toggleModal(_, event) {
        if (event.modalName === 'MASS_EDIT') {
          toggleMassEditModal();
        }
        if (event.modalName === 'DELETE_EQUIPMENTS') {
          openDeleteDialog.set(true);
        }
      },
    },
  });

  const [manualAddState, setManualAddState] = useSetState<IManualAddState>({
    addEquipmentModal: false,
    manualAddOpen: false,
    inventoryImport: false,
    importType: 'SpreadSheet',
  });
  const { addEquipmentModal, manualAddOpen, inventoryImport, importType } =
    manualAddState;
  // const [, setManualAddState] classicUseState(false);
  // const [, setManualAddOpen] = classicUseState(false);
  // const [, setInventoryImport] = classicUseState(false);

  // const [, setImportType] = classicUseState<ImportTypes>('SpreadSheet');
  const { filter, inventory } = useAppSelector();
  const { studioName } = useContext(NavigationContext);
  // Todo: invalidate better. not part of this.
  const equipmentsQuery = useInfiniteQuery(
    ['inventory', 'equipments', 'pages', filter.timing.updatedAt],
    async ({ pageParam = 1 }) => {
      // What happens is we update the filter so we can search.
      const studioInventory = await getEquipments({
        params: makeParamsObject(
          {
            page: pageParam,
            limit: 20,
            search: filter.searchBarText,
          },
          getFilterRequest(filter.options, inventory.activeTab === 'equipment'),
        ),
      });

      return studioInventory;
    },
    {
      onSuccess: result => {
        if (result) {
          const { searchBarText: q } = filter;
          dispatch(updateFilterLastSearch(q));
          if (
            filter.options.direction ||
            filter.options.endDate ||
            filter.options.manufactorNames.length > 0 ||
            filter.options.modelsName.length > 0 ||
            filter.options.sort ||
            filter.options.startDate ||
            filter.options.status.length > 0 ||
            filter.options.tags.length > 0 ||
            filter.options.users.length > 0 ||
            q.length > 0
          ) {
            // Results found -> first page is nonempty.
            const resultsFound = Number(result.pages.at(0)?.total) > 0;
            console.log(filter.options);
            trackInventorySearch(studioName, resultsFound, q, filter.options);
            dispatch(toggleOff('Inventory Search'));
          }
        }
      },
      getNextPageParam: lastPage =>
        lastPage.hasMore ? lastPage.currentPage + 1 : undefined,
    },
  );
  const deleteMassEquipmentsMutation = useMutation(deleteMassEquipments, {
    onSuccess: () => {
      // Deselect all, same as successful mass edit
      queryClient.invalidateQueries(INVENTORY_KEY_PREFIX);
      send('COMPLETE');
      openDeleteDialog.set(false);
    },
  });

  const { data: equipmentsData, isLoading: equipmentsLoading } =
    equipmentsQuery;
  const { filter: pickerFilter } = state.context;
  const { editFilter: massEditParams } = pickerFilter;

  useQuery(
    [
      SELECT_ALL_KEY,
      pickerFilter.queryFilter,
      pickerFilter.queryFilterUpdatedAt,
    ],
    async () => {
      const allEquipments = await getEquipments({
        params: pickerFilter.queryFilter,
      });
      return allEquipments;
    },
    {
      // Only enabled if select all loading
      enabled: state.matches('picking.select-all.loading'),
      onSuccess: newData => {
        const equipmentMap: EquipmentsMap = new Map();
        newData.results.forEach(equipment => {
          equipmentMap.set(equipment.id, equipment);
        });
        // will make select all stop loading.
        send({
          type: 'DONE',
          data: equipmentMap,
        });
      },
      onError() {
        // Log out.
        send({ type: 'STOP-SELECT-ALL' });
      },
    },
  );

  const startSearch = () => {
    const now = new Date().toJSON();
    dispatch(updateFilterUpdatedAt(now));
  };

  const onDoneCb = useCallback(onDoneFn, [send]);

  const equipmentCount = equipmentsData?.pages?.[0]?.total ?? -1;
  const { context } = state;
  const onlyGroupEdits = context.data.size > GROUP_EDIT_LIMIT;

  const showMassEditOpts = isMassEditing(state);

  const isOnboarding = state.context.lastAction === 'LAUNCH';
  const onDelete = useCallback(
    onDeleteEquipments(context, deleteMassEquipmentsMutation),
    [context, deleteMassEquipmentsMutation],
  );

  const updateEquipmentsForOnboarding = (
    equipment: NeededFromEq,
    selected: boolean,
  ): void => {
    if (selected) {
      send({ type: 'REMOVE', equipmentId: equipment.id });
      return;
    }
    send({ type: 'ADD', data: equipment });
  };
  return (
    <Container>
      <Heading
        isLoading={equipmentsLoading}
        state={state}
        send={send}
        resultCount={equipmentCount}
        startSearch={startSearch}
        className="new-icon reset-global"
        broadcast={setManualAddState}
      />
      {inventory.activeTab === 'equipment' && (
        <ListProducts
          result={equipmentsQuery}
          onlyGroupEdits={onlyGroupEdits}
          isOnBoarding={showMassEditOpts}
          equipmentsForOnBoarding={context.data}
          updateEquipmentsForOnBoarding={updateEquipmentsForOnboarding}
          startSearch={startSearch}
        />
      )}

      {addEquipmentModal && (
        <AddEquipmentModal
          open={addEquipmentModal}
          onClose={() =>
            setManualAddState({
              addEquipmentModal: false,
            })
          }
          send={send}
          refreshProducts={startSearch}
        />
      )}

      {manualAddOpen && (
        <ManualAdd
          open={manualAddOpen}
          onClose={() => {
            setManualAddState({
              manualAddOpen: false,
            });
          }}
          send={send}
        />
      )}

      {massEditModal && (
        <OnBoardingModal
          onDone={onDoneCb}
          mode={getEditMode(context, massEditParams)}
          isOnboarding={isOnboarding}
          equipments={state.context.data}
          queryParams={massEditParams}
          deselectedItems={state.context.deselectedItems}
        />
      )}
      {openDeleteDialog.value && (
        <DeleteDialog
          open={openDeleteDialog}
          onCancel={() => {
            send({ type: 'CANCEL' });
          }}
          onDelete={onDelete}
        />
      )}
      {inventoryImport && (
        <ImportInventory
          open={inventoryImport}
          onClose={() => {
            setManualAddState({
              inventoryImport: false,
            });
          }}
          docType={importType}
        />
      )}
    </Container>
  );
  function onDoneFn(
    stateContext: MassEditContext,
    event: ExtractEvent<MassEditEvents, 'TERMINATE' | 'FORCE-TERMINATE'>,
  ): void {
    const { equipments: editedEquipments } = stateContext;
    const { reason } = event;
    if (reason === 'ADD-MORE') {
      send({
        type: 'RESUME',
        data: editedEquipments,
      });
    } else {
      send({
        type: 'COMPLETE',
      });
    }
    toggleMassEditModal();
    dispatch(resetKnownData());
  }
};
export { Products };
